import { defineStore } from "pinia";
import { Delivery, emptyDelivery, type DeliveryEmail } from "~/entities/Delivery";
import type {
  DeliveryStatus,
  IDeliveryRepository,
  IDeliverySaveBody,
  IDeliverySaveBodyCondition,
  IDeliveryTestDeliveryBody,
} from "~/interfaces/IDeliveryRepository";
import type { BulkDeliveryResponse } from "~/entities/BulkDeliveryResponse";
import { DeliveryRepository } from "~/Repositories/DeliveryRepository";
import type { UserTagType } from "~/entities/UserTagType";
import type { StepDeliveryResponse } from "~/entities/StepDeliveryResponse";

/**
 * キャンペインを扱うクラス
 */
export const useDeliveryStore = defineStore("DeliveryStore", () => {
  const bulkDeliveryResponse = ref<BulkDeliveryResponse>();
  const stepDeliveryResponse = ref<StepDeliveryResponse>();
  const accountPlans = ref<{ planId: string; planTitle: string }[]>([]);
  const mailTemplates = ref<DeliveryEmail[]>([]);
  const tagTypes = ref<UserTagType[]>([]);
  const selectedDelivery = ref<Delivery>(emptyDelivery());
  const loading = ref(false);
  const accountPlansLoading = ref(false);
  const tagTypesLoading = ref(false);
  const mailTemplatesLoading = ref(false);

  const repository: IDeliveryRepository = new DeliveryRepository();

  const getBulkDeliveries = async (options: Record<string, any>) => {
    loading.value = true;
    try {
      bulkDeliveryResponse.value = await repository.getBulkDeliveries(options);
      loading.value = false;
    } catch (error: any) {
      loading.value = false;
    }
  };

  const getStepDeliveries = async (options: Record<string, any>) => {
    loading.value = true;
    try {
      stepDeliveryResponse.value = await repository.getStepDeliveries(options);
      loading.value = false;
    } catch (error: any) {
      loading.value = false;
    }
  };

  const changeDeliveryStatus = async (deliveryId: number, status: DeliveryStatus) => {
    await repository.changeDeliveryStatus(String(deliveryId), status);
  };

  const getDelivery = (deliveryId: string) => {
    return repository.getDelivery(String(deliveryId));
  };

  const createDelivery = async (develiry: IDeliverySaveBody) => {
    await repository.createDelivery(develiry);
  };

  const updateDelivery = async (deliveryId: number, develiry: IDeliverySaveBody) => {
    await repository.updateDelivery(String(deliveryId), develiry);
  };

  const deleteDelivery = async (deliveryId: number) => {
    await repository.deleteDelivery(String(deliveryId));
  };

  const getUserCount = async (conditions: IDeliverySaveBodyCondition[]) => {
    return await repository.getUserCount(conditions);
  };

  const getAccountPlans = async () => {
    accountPlansLoading.value = true;
    try {
      accountPlans.value = await repository.getAccountPlans();
      accountPlansLoading.value = false;
    } catch (error: any) {
      accountPlansLoading.value = false;
    }
  };

  const getMailTemplates = async () => {
    accountPlansLoading.value = true;
    try {
      mailTemplates.value = await repository.getMailTemplates();
      mailTemplatesLoading.value = false;
    } catch (error: any) {
      mailTemplatesLoading.value = false;
    }
  };

  const getTagTypes = async () => {
    tagTypesLoading.value = true;
    try {
      const res = await repository.getTagTypes();
      tagTypes.value = res.userTagTypes;
      tagTypesLoading.value = false;
    } catch (error: any) {
      tagTypesLoading.value = false;
    }
  };

  const testDelivery = async (body: IDeliveryTestDeliveryBody) => {
    await repository.testDelivery(body);
  };

  return {
    loading,
    accountPlansLoading,
    tagTypesLoading,
    mailTemplatesLoading,
    selectedDelivery,
    bulkDeliveryResponse,
    stepDeliveryResponse,
    accountPlans,
    tagTypes,
    mailTemplates,
    getDelivery,
    changeDeliveryStatus,
    getBulkDeliveries,
    getStepDeliveries,
    createDelivery,
    updateDelivery,
    deleteDelivery,
    getAccountPlans,
    getMailTemplates,
    getTagTypes,
    getUserCount,
    testDelivery,
  };
});
